import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Space, Table, Tag } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { getMonitorApiBaseUrl } from '../../config/env';
import { getContainerHealth } from './api';
import { ContainerHealthItem } from './types';
import { formatDateTime } from '../../utils/format';

const SERVICES = ['Postgres', 'Redis', 'Backend', 'Tracking writer', 'Tracking progress', 'Caddy'] as const;
type Service = typeof SERVICES[number];
const colors: Record<string, string> = { healthy: 'green', running: 'blue', stale: 'orange', missing: 'red', unhealthy: 'red', exited: 'red', dead: 'red', restarting: 'orange', starting: 'orange', paused: 'orange' };

export function serviceContainers(items: ContainerHealthItem[], service: Service): ContainerHealthItem[] {
  const matched = items.filter((item) => {
    const name = (item.container_name || '').toLowerCase().replace(/^\//, '');
    const tokens = name.split(/[-_.]/);
    switch (service) {
      case 'Postgres': return tokens.some((token) => ['postgres', 'postgresql', 'timescaledb'].includes(token));
      case 'Redis': return tokens.includes('redis');
      case 'Backend': return tokens.includes('backend') && !tokens.some((token) => ['monitor', 'tracking'].includes(token));
      case 'Tracking writer': return tokens.includes('writer') && (tokens.includes('tracking') || name === 'writer');
      case 'Tracking progress': return tokens.some((token) => ['progress', 'processor'].includes(token)) && (tokens.includes('tracking') || ['progress', 'processor'].includes(name));
      case 'Caddy': return tokens.includes('caddy');
      default: return false;
    }
  });
  return matched.sort((a, b) => (a.container_name || '').localeCompare(b.container_name || ''));
}

export function serviceStatus(items: ContainerHealthItem[]): string {
  if (!items.length) return 'unobserved';
  const statuses: string[] = items.map((item) => item.status);
  return ['missing', 'stale', 'unhealthy', 'dead', 'exited', 'restarting', 'paused', 'removing', 'created', 'starting', 'unknown', 'running', 'healthy'].find((status) => statuses.includes(status)) || 'unknown';
}

export const ContainerHealthPanel: React.FC = () => {
  const baseURL = getMonitorApiBaseUrl();
  const query = useQuery({
    queryKey: ['monitor-container-health', baseURL],
    queryFn: ({ signal }) => getContainerHealth(signal),
    enabled: !!baseURL, refetchInterval: 30000, retry: false,
  });
  const data = query.isError ? undefined : query.data;
  const items = data?.items || [];
  const historical = items.length > 0 && items.every((item) => item.age_seconds > (data?.stale_seconds ?? 300));
  const latestSeen = items.reduce((latest, item) => Math.max(latest, Date.parse(item.last_seen)), 0);
  const rows = SERVICES.map((name) => {
    const containers = serviceContainers(items, name);
    return { name, containers, status: serviceStatus(containers) };
  });
  return <Card size="small" title="Container health" extra={
    <Button icon={<ReloadOutlined />} disabled={!baseURL} loading={query.isFetching} onClick={() => query.refetch()}>Refresh health</Button>
  }>
    <Space direction="vertical" size="middle" className="w-full">
      {!baseURL ? <Alert type="info" showIcon message="Monitor API is not configured for this environment" />
        : query.isError && <Alert type="error" showIcon message="Unable to load container health"
          description={query.error instanceof Error ? query.error.message : 'Container health request failed'} />}
      {historical && <Alert type="warning" showIcon message="No current telemetry — showing latest recorded container data"
        description={`Last recorded: ${formatDateTime(latestSeen)}. These values are historical.`} />}
      <Table rowKey="name" dataSource={rows} pagination={false} size="small" scroll={{ x: 800 }} columns={[
        { title: 'Service', dataIndex: 'name', width: 160 },
        { title: 'Status', width: 130, render: (_, row) => !data
          ? <Tag>{query.isPending && baseURL ? 'Loading' : 'Unknown'}</Tag>
          : row.status === 'unobserved' ? <Tag>No telemetry</Tag>
            : <Tag color={colors[row.status]} title={row.containers.map((item) => `Docker state: ${item.state || 'unknown'}; health: ${item.health_status || 'no health data'}`).join('\n')}>{row.status.charAt(0).toUpperCase() + row.status.slice(1)}</Tag> },
        { title: 'Restarts / started at', render: (_, row) => <Space direction="vertical" size={0}>
          {row.containers.length ? row.containers.map((item) => {
            const startedAt = item.uptime_seconds == null ? null : new Date(Date.parse(item.uptime_last_seen || item.last_seen) - item.uptime_seconds * 1000);
            return <span key={item.container_id + item.host_name}>
              {item.restarts ?? '—'} / {startedAt ? <time dateTime={startedAt.toISOString()}>{formatDateTime(startedAt.toISOString())}</time> : '—'}
            </span>;
          }) : '—'}
        </Space> },
        { title: 'Telemetry / last seen', render: (_, row) => <Space direction="vertical" size={0}>
          {row.containers.length ? row.containers.map((item) => <span key={item.container_id + item.host_name}>
            <Tag color={colors[item.telemetry_status]}>{item.telemetry_status === 'healthy' ? 'fresh' : item.telemetry_status}</Tag> {formatDateTime(item.last_seen)} · {item.age_seconds}s ago
          </span>) : '—'}
        </Space> },
      ]} />
    </Space>
  </Card>;
};
